import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { App as AntdApp } from 'antd';
import { XProvider } from '@ant-design/x';

import { theme } from '@/styles';
import { HEADER_HEIGHT, SPACING_3 } from '@/styles/tokens';
import { useBreakpoint } from '@/hooks';
import { useHeaderContentStore } from '@/store';
import '@/styles/global.scss';

import { MainLayout, AuthLayout } from '@/layouts';
import { ProtectedRoute, PublicRoute, RequireRole } from '@/components';
import {
  Login,
  Redeem,
  Register,
  VerifyDevice,
  ResetPassword,
  ResetPasswordConfirm,
  Dashboard,
  Sessions,
  SessionInterview,
  SessionResults,
  Packages,
  Admin,
  AdminUsers,
  AdminAssessments,
  AdminQuestions,
  AdminQuestionSets,
  AdminPackages,
  AdminTraces,
  AdminTraceDetail,
  AdminHtsQuestionnaires,
  Evaluate,
  AdminSessionResults,
  AdminHtsSubmissionResults,
  HtsQuestionnaire,
  HtsReadiness,
  StyleGuide,
  NotFound,
} from '@/pages';
import { ROUTES } from '@/routes';
import { USER_ROLES } from '@/constants';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn(
    'Missing VITE_CLERK_PUBLISHABLE_KEY environment variable. ' +
    'Authentication will not work. Please add it to your .env file.'
  );
}

const ROOT_FONT_SIZE_PX = 16;
const toPx = (token: string): number =>
  token.trim().endsWith('rem') ? parseFloat(token) * ROOT_FONT_SIZE_PX : parseFloat(token);

const NOTIFICATION_GAP = toPx(SPACING_3);
const FALLBACK_HEADER_HEIGHT = toPx(HEADER_HEIGHT);

export const App: React.FC = () => {
  const { isMobile } = useBreakpoint();
  const headerBottom = useHeaderContentStore((s) => s.bottomOffset);

  const notificationConfig = React.useMemo(
    () =>
      isMobile
        ? {
            placement: 'top' as const,
            top: (headerBottom ?? FALLBACK_HEADER_HEIGHT) + NOTIFICATION_GAP,
          }
        : { placement: 'bottomRight' as const },
    [isMobile, headerBottom]
  );

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY || ''}>
      <XProvider theme={theme}>
        <AntdApp notification={notificationConfig}>
          <Router>
            <Routes>
              {/* Standalone: a guest redeeming a code has no Clerk session, so this fits
                  neither ProtectedRoute nor PublicRoute (which redirects signed-in users
                  away — an admin minting test codes needs to reach it). */}
              <Route path={ROUTES.REDEEM} element={<Redeem />} />

              {/* Temporary — quick typography reference, reachable regardless of auth
                  state. Safe to delete this route once no longer needed. */}
              {import.meta.env.DEV && <Route path={ROUTES.STYLE_GUIDE} element={<StyleGuide />} />}

              <Route element={<PublicRoute />}>
                <Route element={<AuthLayout />}>
                  <Route path={ROUTES.LOGIN} element={<Login />} />
                  <Route path={ROUTES.REGISTER} element={<Register />} />
                  <Route path={ROUTES.VERIFY_DEVICE} element={<VerifyDevice />} />
                  <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
                  <Route path={ROUTES.RESET_PASSWORD_CONFIRM} element={<ResetPasswordConfirm />} />
                </Route>
              </Route>

              <Route element={<ProtectedRoute />}>
                {/* Full-screen route — no sidebar/topbar */}
                <Route path={ROUTES.HTS_QUESTIONNAIRE} element={<HtsQuestionnaire />} />

                <Route element={<MainLayout />}>
                  {import.meta.env.DEV && <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />}
                  <Route path={ROUTES.SESSIONS} element={<Sessions />} />
                  <Route path={ROUTES.SESSION_INTERVIEW} element={<SessionInterview />} />
                  <Route path={ROUTES.SESSION_RESULTS} element={<SessionResults />} />
                  {/* Same components as above, reused verbatim for a code-redeemed guest
                      under a distinct URL — see hooks/useSessionRoutes.ts. */}
                  <Route path={ROUTES.GUEST_SESSIONS} element={<Sessions />} />
                  <Route path={ROUTES.GUEST_SESSION_INTERVIEW} element={<SessionInterview />} />
                  <Route path={ROUTES.GUEST_SESSION_RESULTS} element={<SessionResults />} />
                  <Route path={ROUTES.HTS_READINESS} element={<HtsReadiness />} />
                  <Route path={ROUTES.PACKAGES} element={<Packages />} />
                  {import.meta.env.DEV && (
                    <Route path={ROUTES.SETTINGS} element={<div>Settings Page</div>} />
                  )}
                  {import.meta.env.DEV && (
                    <Route path={ROUTES.PROFILE} element={<div>Profile Page</div>} />
                  )}
                </Route>

                <Route
                  element={
                    <RequireRole roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.COACH]} />
                  }
                >
                  <Route element={<MainLayout />}>
                    <Route path={ROUTES.ADMIN_ASSESSMENTS} element={<AdminAssessments />} />
                    <Route path={ROUTES.ADMIN_SESSION_RESULTS} element={<AdminSessionResults />} />
                    <Route path={ROUTES.ADMIN_HTS_SUBMISSION_RESULTS} element={<AdminHtsSubmissionResults />} />
                  </Route>
                </Route>

                <Route
                  element={
                    <RequireRole roles={[USER_ROLES.SUPER_ADMIN]} />
                  }
                >
                  <Route element={<MainLayout />}>
                    <Route path={ROUTES.ADMIN} element={<Admin />} />
                    <Route path={ROUTES.EVALUATE} element={<Evaluate />} />
                    <Route path={ROUTES.ADMIN_USERS} element={<AdminUsers />} />
                    <Route path={ROUTES.ADMIN_QUESTIONS} element={<AdminQuestions />} />
                    <Route path={ROUTES.ADMIN_QUESTION_SETS} element={<AdminQuestionSets />} />
                    <Route path={ROUTES.ADMIN_QUESTION_SET_NEW} element={<AdminQuestionSets />} />
                    <Route path={ROUTES.ADMIN_QUESTION_SET_EDIT} element={<AdminQuestionSets />} />
                    <Route path={ROUTES.ADMIN_HTS_QUESTIONNAIRES} element={<AdminHtsQuestionnaires />} />
                    <Route path={ROUTES.ADMIN_HTS_QUESTIONNAIRE_NEW} element={<AdminHtsQuestionnaires />} />
                    <Route path={ROUTES.ADMIN_HTS_QUESTIONNAIRE_EDIT} element={<AdminHtsQuestionnaires />} />
                    <Route path={ROUTES.ADMIN_PACKAGES} element={<AdminPackages />} />
                    <Route path={ROUTES.ADMIN_PACKAGE_NEW} element={<AdminPackages />} />
                    <Route path={ROUTES.ADMIN_PACKAGE_EDIT} element={<AdminPackages />} />
                    <Route path={ROUTES.ADMIN_TRACES} element={<AdminTraces />} />
                    <Route path={ROUTES.ADMIN_TRACE_DETAIL} element={<AdminTraceDetail />} />
                    {import.meta.env.DEV && (
                      <Route
                        path={ROUTES.ADMIN_ORGANIZATIONS}
                        element={<div>Organization Management</div>}
                      />
                    )}
                  </Route>
                </Route>
              </Route>

              <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />
              <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AntdApp>
      </XProvider>
    </ClerkProvider>
  );
};

export default App;
